/*
 * Cart drawer customisations. Loaded from sections/cart-drawer.liquid.
 * - <cart-checkout-total>: total shown inside the checkout button
 * - <cart-upsell-slider>: "you may also like" slider (snippets/cart-drawer-upsell.liquid)
 * - <cart-reserve-timer>: countdown in the banner of the drawer (custom code block)
 * - Quick add opened from the slider: closing it reopens the cart drawer
 * The first two refresh on the theme's `theme:cart:change` event, which the theme fires after
 * re-rendering the line items, the upsell products and the cart price.
 */

(function() {
  const priceTotalSelector = '[data-cart-price-holder] [data-cart-total]';

  if (!customElements.get('cart-checkout-total')) {
    class CartCheckoutTotal extends HTMLElement {
      constructor() {
        super();
        this.onCartChangeCallback = this.update.bind(this);
      }

      connectedCallback() {
        document.addEventListener('theme:cart:change', this.onCartChangeCallback);
      }

      disconnectedCallback() {
        document.removeEventListener('theme:cart:change', this.onCartChangeCallback);
      }

      update() {
        const total = document.querySelector(priceTotalSelector);
        if (!total || !window.theme?.formatMoney) return;

        const cents = parseInt(total.dataset.cartTotal, 10) || 0;
        this.textContent = cents === 0 ? window.theme.strings?.free || total.textContent.trim() : window.theme.formatMoney(cents, window.theme.moneyFormat);
      }
    }

    customElements.define('cart-checkout-total', CartCheckoutTotal);
  }

  if (!customElements.get('cart-upsell-slider')) {
    const selectors = {
      track: '[data-upsell-products]',
      card: '[data-quick-add-holder]',
      prev: '[data-upsell-prev]',
      next: '[data-upsell-next]',
      cartItem: 'cart-drawer [data-item][data-product-id]',
      recommendations: '[data-cart-upsell-recommendations]',
    };

    class CartUpsellSlider extends HTMLElement {
      constructor() {
        super();
        this.cache = new Map();
        this.onCartChangeCallback = this.refresh.bind(this);
        this.onScrollCallback = this.updateArrows.bind(this);
      }

      connectedCallback() {
        this.track = this.querySelector(selectors.track);
        this.prev = this.querySelector(selectors.prev);
        this.next = this.querySelector(selectors.next);

        this.prev?.addEventListener('click', () => this.go(-1));
        this.next?.addEventListener('click', () => this.go(1));
        this.track?.addEventListener('scroll', this.onScrollCallback, {passive: true});
        document.addEventListener('theme:cart:change', this.onCartChangeCallback);

        this.refresh();
      }

      disconnectedCallback() {
        document.removeEventListener('theme:cart:change', this.onCartChangeCallback);
      }

      /**
       * Upsell products from the cart items' metafields win; otherwise use the recommendations
       * of the first cart item. The cached HTML is inserted in the same task as the theme's
       * re-render, so the slider doesn't flicker on quantity changes.
       */
      refresh() {
        if (!this.track) return;

        const cartItems = Array.from(document.querySelectorAll(selectors.cartItem));
        const cartProductIds = new Set(cartItems.map((item) => item.dataset.productId));
        const productId = cartItems[0]?.dataset.productId;
        // The theme replaces the track content on every cart change, so recommendation cards only carry the marker until then
        const hasOwnUpsells = this.track.querySelector(`${selectors.card}:not([data-recommendation])`);

        if (hasOwnUpsells || !productId) {
          this.render(cartProductIds);
          return;
        }

        if (this.cache.has(productId)) {
          this.fill(this.cache.get(productId), cartProductIds);
          return;
        }

        const requestId = (this.requestId = (this.requestId || 0) + 1);
        const url = `${this.dataset.url}?product_id=${encodeURIComponent(productId)}&limit=8&section_id=api-cart-upsell`;

        fetch(url)
          .then((response) => (response.ok ? response.text() : ''))
          .then((html) => {
            const element = document.createElement('div');
            element.innerHTML = html;
            const content = element.querySelector(selectors.recommendations)?.innerHTML.trim() || '';

            this.cache.set(productId, content);
            if (requestId === this.requestId) this.fill(content, cartProductIds);
          })
          .catch(() => this.render(cartProductIds));
      }

      fill(html, cartProductIds) {
        this.track.innerHTML = html;
        this.track.querySelectorAll(selectors.card).forEach((card) => card.setAttribute('data-recommendation', ''));
        this.render(cartProductIds);
      }

      render(cartProductIds) {
        // Never suggest a product that is already in the cart
        this.track.querySelectorAll(selectors.card).forEach((card) => {
          if (cartProductIds.has(card.getAttribute('data-quick-add-holder'))) card.parentElement.remove();
        });

        const count = this.track.querySelectorAll(selectors.card).length;
        this.classList.toggle('is-hidden', count === 0);
        this.classList.toggle('cart-upsell--single', count < 2);
        this.track.scrollLeft = 0;
        this.updateArrows();
      }

      go(direction) {
        this.track.scrollBy({left: direction * this.track.clientWidth, behavior: 'smooth'});
      }

      // A class, not `disabled`: the theme re-enables every button of the drawer after a cart update
      updateArrows() {
        if (!this.prev || !this.next) return;

        const maxScroll = this.track.scrollWidth - this.track.clientWidth - 2;
        this.toggleArrow(this.prev, this.track.scrollLeft <= 2);
        this.toggleArrow(this.next, this.track.scrollLeft >= maxScroll);
      }

      toggleArrow(arrow, inactive) {
        arrow.classList.toggle('is-inactive', inactive);
        arrow.setAttribute('aria-disabled', inactive);
      }
    }

    customElements.define('cart-upsell-slider', CartUpsellSlider);
  }

  /*
   * Countdown shown as mm:ss. It starts the first time the cart drawer opens with products in this
   * browser session (the end time is kept in sessionStorage, so it survives page changes). When it
   * runs out the whole banner is hidden, like the Maison Vallier cart does; it never restarts on its own.
   */
  if (!customElements.get('cart-reserve-timer')) {
    const storageKey = 'cpCartTimerEnd';

    class CartReserveTimer extends HTMLElement {
      constructor() {
        super();
        this.onDrawerOpenCallback = this.start.bind(this);
      }

      connectedCallback() {
        this.minutes = parseInt(this.dataset.minutes, 10) || 10;

        try {
          this.end = parseInt(sessionStorage.getItem(storageKey), 10) || null;
        } catch (error) {
          this.end = null;
        }

        if (this.end) {
          this.run();
        } else {
          this.render(this.minutes * 60);
          document.addEventListener('theme:cart-drawer:open', this.onDrawerOpenCallback);
        }
      }

      disconnectedCallback() {
        clearInterval(this.interval);
        document.removeEventListener('theme:cart-drawer:open', this.onDrawerOpenCallback);
      }

      start() {
        // An empty cart has nothing to count down for: wait for the next opening
        if (this.end || this.closest('cart-drawer')?.classList.contains('is-empty')) return;

        document.removeEventListener('theme:cart-drawer:open', this.onDrawerOpenCallback);
        this.end = Date.now() + this.minutes * 60 * 1000;
        try {
          sessionStorage.setItem(storageKey, String(this.end));
        } catch (error) {
          // Without storage the countdown still runs until the next page load
        }
        this.run();
      }

      run() {
        this.tick();
        this.interval = setInterval(() => this.tick(), 1000);
      }

      tick() {
        const seconds = Math.max(0, Math.round((this.end - Date.now()) / 1000));
        this.render(seconds);
        if (seconds > 0) return;

        clearInterval(this.interval);
        const banner = this.closest('.cart-block') || this.closest('.cp-cart-alert');
        if (banner) banner.style.display = 'none';
      }

      render(seconds) {
        const pad = (value) => String(value).padStart(2, '0');
        this.textContent = `${pad(Math.floor(seconds / 60))}:${pad(seconds % 60)}`;
      }
    }

    customElements.define('cart-reserve-timer', CartReserveTimer);
  }

  /*
   * The theme closes the cart drawer when a quick add opens (`theme:quick-add:open`) and leaves it
   * closed. When the quick add was opened from the slider, reopen the drawer once the dialog loses
   * its `open` attribute (X, Escape or backdrop click). After "add to cart" the theme already opens it.
   * The dialog's native `close` event isn't reliable here, so watch the attribute instead.
   */
  if (!window.cartUpsellQuickAddReturn) {
    window.cartUpsellQuickAddReturn = true;
    let openedFromUpsell = false;

    document.addEventListener('click', (event) => {
      if (event.target.closest('cart-upsell-slider [data-quick-add-modal-handle]')) openedFromUpsell = true;
    }, true);

    new MutationObserver((mutations) => {
      mutations.forEach(({target}) => {
        if (!openedFromUpsell || target.hasAttribute('open') || !target.matches('dialog.product-quick-add')) return;

        openedFromUpsell = false;
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && !cartDrawer.cartDrawerIsOpen) cartDrawer.dispatchEvent(new CustomEvent('theme:cart-drawer:show'));
      });
    }).observe(document.body, {attributes: true, attributeFilter: ['open'], subtree: true});
  }
})();
